import { API_CONFIG } from '../config/settings'

export interface VendorLoginResponse {
  success: boolean
  message?: string
  vendor?: {
    id: string
    username: string
    email: string
    status: number
    name?: string | null
    is_first_login?: boolean
  }
  bridge_token?: string
  redirect?: string
  detail?: string
}

function getCsrfTokenFromCookie(): string {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/)
  return match ? decodeURIComponent(match[1]) : ''
}

/** Prime Laravel session + XSRF-TOKEN cookie (same origin). */
export async function warmLaravelSession(): Promise<void> {
  await fetch('/vendor/api/csrf', {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
  })
}

/** FastAPI: validate credentials and return bridge token. */
export async function loginVendor(username: string, password: string): Promise<VendorLoginResponse> {
  const response = await fetch(`${API_CONFIG.BASE_URL}/vendor/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ username: username.trim(), password }),
  })

  const data: VendorLoginResponse = await response.json()

  if (!response.ok) {
    throw new Error(data.detail || data.message || 'Login failed')
  }

  return data
}

/** Laravel: create vendor guard session from FastAPI bridge token. */
export async function establishVendorSession(bridgeToken: string): Promise<{ success: boolean; redirect?: string; message?: string }> {
  await warmLaravelSession()

  const response = await fetch('/vendor/api/establish-session', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-XSRF-TOKEN': getCsrfTokenFromCookie(),
    },
    body: JSON.stringify({ bridge_token: bridgeToken }),
  })

  const data = await response.json()

  if (!response.ok || !data.success) {
    throw new Error(data.message || 'Could not establish session')
  }

  return data
}

/** Full host login: FastAPI auth + Laravel session cookie. */
export async function completeVendorLogin(username: string, password: string): Promise<string> {
  const login = await loginVendor(username, password)

  if (!login.bridge_token) {
    throw new Error('Login succeeded but no session token was returned')
  }

  const session = await establishVendorSession(login.bridge_token)
  return session.redirect || login.redirect || '/vendor/dashboard'
}

const VENDOR_PASSWORD_MIN_LEN = 8
const VENDOR_PASSWORD_MAX_LEN = 32

/** Returns an error message, or null when valid. */
export function validateVendorPassword(password: string): string | null {
  if (!password) return 'Password is required'
  if (password.length < VENDOR_PASSWORD_MIN_LEN) {
    return `Password must be at least ${VENDOR_PASSWORD_MIN_LEN} characters`
  }
  if (password.length > VENDOR_PASSWORD_MAX_LEN) {
    return `Password must not exceed ${VENDOR_PASSWORD_MAX_LEN} characters`
  }
  if (!/^[A-Za-z0-9]+$/.test(password)) {
    return 'Password must contain only letters and numbers (alphanumeric)'
  }
  if (!/[A-Za-z]/.test(password)) {
    return 'Password must contain at least one letter'
  }
  if (!/\d/.test(password)) {
    return 'Password must contain at least one number'
  }
  return null
}

/** Step 1: send temporary reset code to vendor email. */
export async function requestVendorPasswordReset(email: string): Promise<void> {
  const response = await fetch(`${API_CONFIG.BASE_URL}/vendor/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email: email.trim() }),
  })

  const data = await response.json()

  if (!response.ok) {
    throw new Error(data.detail || data.message || 'Failed to send reset code')
  }
}

/** Step 2: verify code and set new password. */
export async function resetVendorPassword(
  email: string,
  code: string,
  newPassword: string,
  confirmPassword: string,
): Promise<void> {
  const response = await fetch(`${API_CONFIG.BASE_URL}/vendor/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      email: email.trim(),
      code: code.trim(),
      new_password: newPassword,
      confirm_password: confirmPassword,
    }),
  })

  const data = await response.json()

  if (!response.ok) {
    throw new Error(data.detail || data.message || 'Failed to reset password')
  }
}
